import { Component, ChangeDetectionStrategy } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";

import type { ICellRendererAngularComp } from "ag-grid-angular";
import type { ICellRendererParams } from "ag-grid-community";

import { HasRoleDirective } from "../../directives/has-role.directive";

export interface DeleteRowCellRendererParams<T> extends ICellRendererParams<T> {
  onDelete: (row: T) => void;
  getLabel: (row: T) => string;
}

/** The subset of `DeleteRowCellRendererParams` callers supply via `cellRendererParams` — the rest is filled in by AG Grid itself. */
export type DeleteRowCellRendererCustomParams<T> = Pick<DeleteRowCellRendererParams<T>, "onDelete" | "getLabel">;

@Component({
  selector: "delete-row-cell-renderer",
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, HasRoleDirective],
  template: `
    <button
      *hasRole="'canDelete'"
      mat-icon-button
      type="button"
      color="warn"
      [attr.aria-label]="'Delete ' + label"
      [matTooltip]="'Delete ' + label"
      (click)="onDeleteClick($event)"
    >
      <mat-icon>delete</mat-icon>
    </button>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class DeleteRowCellRenderer<T> implements ICellRendererAngularComp {
  #params!: DeleteRowCellRendererParams<T>;

  label = "";

  agInit(params: DeleteRowCellRendererParams<T>): void {
    this.#params = params;
    this.label = params.data ? params.getLabel(params.data) : "";
  }

  refresh(params: DeleteRowCellRendererParams<T>): boolean {
    this.#params = params;
    this.label = params.data ? params.getLabel(params.data) : "";
    return true;
  }

  onDeleteClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.#params.data) {
      this.#params.onDelete(this.#params.data);
    }
  }
}
